import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from "xlsx";
import mammoth from "mammoth";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist/legacy/build/pdf";
import { jsPDF } from "jspdf";
import emailjs from "@emailjs/browser";
import { REGISTER_URL, STAFF_URL, COMPANY_URL } from "../../apiConfig";
import { Lock, Loader2, CheckCircle2, Check, ClipboardList, ArrowDown, AlertTriangle } from "lucide-react";

// Initialize EmailJS
emailjs.init("ns8iBr8YNGxt5OpPN");

// Set PDF worker (works using CDN). Adjust if you host worker locally.
GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${(getDocument.version || "3.9.179")}/pdf.worker.min.js`;

// Helper function to load logo as base64
const loadLogoAsBase64 = async () => {
  try {
    const response = await fetch('/logo.png');
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error loading logo:', error);
    return null;
  }
};

export default function ClientRegistrationForm({ courseData }) {
  const blankStudent = {
    name: "",
    idType: "NRIC",
    nric: "",
    dob: "",
    email: "",
    gender: "",
    nationality: "",
    race: "",
    contact: "",
    educationCert: null,
    workPermit: null,
  };

  const [students, setStudents] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [loadingIndexes, setLoadingIndexes] = useState({});
  const [bulkLoading, setBulkLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // 🟢 CUSTOM ALERT STATE & OVERRIDE
  const [customAlert, setCustomAlert] = useState({ isOpen: false, message: "" });
  const alert = (message) => {
    setCustomAlert({ isOpen: true, message: String(message) });
  };

  // 🟢 GOOGLE RECAPTCHA v3 STATE
  const [recaptchaToken, setRecaptchaToken] = useState("");

  // 🟢 FORM DATA WITH AUTO-FETCHED COURSE INFO
  const [formData, setFormData] = useState({
    companyName: "",
    uen: "",
    industry: "",
    type: "",
    agentCompany: "",
    companyAddress: "",
    postalCode: "",
    billingAddress: "",
    clientEmail: "",
    contactPerson: "",
    contactNumber: "",
    password: "",
    confirmPassword: "",
    courseName: courseData?.title || "",
    courseAmount: "0.00",
    promotionCode: "",
    howKnow: "",
    knowStaff: "No",
    staffName: "",
    staffEmail: "",
    agree: false,
  });

  // 🟢 RECAPTCHA v3 SITE KEY - Replace with your actual key
  const RECAPTCHA_SITE_KEY = "6LdhwBcsAAAAAKDrnrh8YCtsmNmn7B2ZOvA7GroR";

  // 🟢 AUTO-FETCH COURSE DATA EFFECT with employee calculation
  useEffect(() => {
    if (courseData?.price) {
      calculateTotalAmount();
    }
  }, [courseData, students.length]);

  // 🟢 CALCULATION FUNCTION - Total amount based on number of students
  const calculateTotalAmount = () => {
    if (!courseData?.price) return;

    const totalAmount = courseData.price * students.length;
    const totalGst = totalAmount * 0.09;
    const grandTotal = totalAmount + totalGst;

    setFormData(prev => ({
      ...prev,
      courseName: courseData.title || "",
      courseAmount: grandTotal.toFixed(2)
    }));
  };

  // 🟢 UEN AUTO-LOOKUP STATE
  const [uenLookupState, setUenLookupState] = useState('idle'); // 'idle' | 'loading' | 'found' | 'not_found' | 'error'
  const uenDebounceTimer = useRef(null);

  // 🟢 UEN AUTO-LOOKUP EFFECT
  useEffect(() => {
    const uen = formData.uen.trim();
    if (uen.length < 6) {
      setUenLookupState('idle');
      return;
    }

    if (uenDebounceTimer.current) clearTimeout(uenDebounceTimer.current);
    setUenLookupState('loading');

    uenDebounceTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${COMPANY_URL}?uen=${encodeURIComponent(uen.toUpperCase())}`);
        const data = await res.json();

        if (data.success && data.found) {
          setUenLookupState('found'); // ALREADY REGISTERED!
        } else {
          setUenLookupState('not_found'); // Safe to register
        }
      } catch (err) {
        console.error('UEN lookup error:', err);
        setUenLookupState('error');
      }
    }, 700);

    return () => clearTimeout(uenDebounceTimer.current);
  }, [formData.uen]);

  // 🟢 SALES STAFF AUTO-LOOKUP STATE
  const [staffLookupState, setStaffLookupState] = useState('idle'); // 'idle' | 'loading' | 'found' | 'not_found' | 'error'
  const [staffOfficialName, setStaffOfficialName] = useState('');
  const staffDebounceTimer = useRef(null);

  // 🟢 SALES STAFF AUTO-LOOKUP EFFECT — debounced, triggers after staffName has 3+ chars
  useEffect(() => {
    const name = formData.staffName.trim();
    if (name.length < 3) {
      setStaffLookupState('idle');
      setStaffOfficialName('');
      return;
    }

    if (staffDebounceTimer.current) clearTimeout(staffDebounceTimer.current);
    setStaffLookupState('loading');

    staffDebounceTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${STAFF_URL}?name=${encodeURIComponent(name)}`);
        const data = await res.json();

        if (data.success && data.found && data.staff) {
          const s = data.staff;
          setStaffLookupState('found');
          setStaffOfficialName(s.staff_name || '');
          // Auto-fill staff fields
          setFormData(prev => ({
            ...prev,
            staffName: s.staff_name || prev.staffName,
            staffEmail: s.email || prev.staffEmail,
          }));
        } else {
          setStaffLookupState('not_found');
          setStaffOfficialName('');
        }
      } catch (err) {
        console.error('Staff lookup error:', err);
        setStaffLookupState('error');
        setStaffOfficialName('');
      }
    }, 700);

    return () => clearTimeout(staffDebounceTimer.current);
  }, [formData.staffName]);

  // 🟢 RECAPTCHA v3 INITIALIZATION
  useEffect(() => {
    const loadRecaptcha = () => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      script.addEventListener('load', () => {
        console.log('reCAPTCHA v3 loaded');
      });
      document.body.appendChild(script);
    };

    loadRecaptcha();
  }, []);

  // 🟢 EXECUTE RECAPTCHA v3
  const executeRecaptcha = async () => {
    try {
      if (!window.grecaptcha) {
        console.error('reCAPTCHA not loaded');
        return null;
      }

      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
        action: 'client_registration'
      });
      setRecaptchaToken(token);
      return token;
    } catch (error) {
      console.error('reCAPTCHA execution failed:', error);
      return null;
    }
  };

  // 🟢 FORM FIELD HANDLER
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "knowStaff" && value === "No") {
      setStaffLookupState('idle');
      setStaffOfficialName('');
      setFormData(prev => ({
        ...prev,
        knowStaff: "No",
        staffName: "",
        staffEmail: "",
      }));
    } else {
      setFormData({
        ...formData,
        [name]: type === "checkbox" ? checked : value,
      });
    }
  };

  const addTrainee = () => {
    // Dynamic capacity calculation
    const maxPax = courseData?.schedule?.max_trainee ? Number(courseData.schedule.max_trainee) : 20;
    const currentPax = courseData?.schedule?.current_trainee ? Number(courseData.schedule.current_trainee) : 0;
    const availableSlots = Math.max(0, maxPax - currentPax);

    if (students.length >= availableSlots) {
      alert(`Maximum capacity of ${availableSlots} trainees reached for this schedule.\nPlease book another schedule for additional trainees.`);
      return;
    }
    setStudents((prev) => [...prev, { ...blankStudent }]);
  };

  // ---------- Basic student handlers ----------
  const addStudent = (student = { ...blankStudent }) => {
    setStudents((s) => [...s, student]);
  };

  const removeStudent = (index) => {
    setStudents((s) => {
      const copy = [...s];
      copy.splice(index, 1);
      return copy.length ? copy : [{ ...blankStudent }];
    });
  };

  const handleStudentChange = (index, field, value) => {
    setStudents((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };


  // 🔥 SUCCESS MODAL COMPONENT
  const SuccessModal = ({ onClose }) => {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-gray-900/75 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.95, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.95, y: 20, opacity: 0 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-100 overflow-hidden text-center flex flex-col p-8"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={36} className="text-green-600 stroke-[3]" />
          </div>
          <h3 className="text-2xl font-black text-gray-900 mb-2">Registration Submitted!</h3>
          <p className="text-gray-600 mb-8">
            Thank you for registering your company <strong>{formData.companyName}</strong>. 
            We have received your details and will review and respond within a few working days.
          </p>
          <button
            onClick={onClose}
            className="w-full bg-[#0b9949] hover:bg-[#087c3a] text-white py-3 rounded-xl font-bold text-sm tracking-wide shadow-md transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            Go to Corporate Home
          </button>
        </motion.div>
      </motion.div>
    );
  };

  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // ---------- Submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    // Validation
    if (!formData.companyName || !formData.uen || !formData.clientEmail || !formData.contactPerson || !formData.contactNumber) {
      alert("Please fill all required company details!");
      return;
    }

    // 🟢 DUPLICATE NRIC VALIDATION
    const nrics = students.map(t => (t.nric || "").toLowerCase().trim()).filter(Boolean);
    const uniqueNrics = new Set(nrics);
    if (nrics.length !== uniqueNrics.size) {
      alert("⚠️ Duplicate Identification Numbers found! You have entered the same NRIC/FIN for multiple trainees. Please ensure each trainee has a unique ID.");
      return;
    }

    // 🟢 DUPLICATE EMAIL VALIDATION
    const emails = students.map(t => (t.email || "").toLowerCase().trim()).filter(Boolean);
    const uniqueEmails = new Set(emails);
    if (emails.length !== uniqueEmails.size) {
      alert("⚠️ Duplicate Emails found! Multiple trainees cannot share the exact same email address. Please ensure each trainee has a unique email.");
      return;
    }

    // 🟢 DUPLICATE CONTACT VALIDATION
    const contacts = students.map(t => (t.contact || "").trim()).filter(Boolean);
    const uniqueContacts = new Set(contacts);
    if (contacts.length !== uniqueContacts.size) {
      alert("⚠️ Duplicate Contact Numbers found! Multiple trainees cannot share the exact same phone number. Please ensure each trainee has a unique contact number.");
      return;
    }

    // 🟢 UEN ALREADY REGISTERED VALIDATION
    if (uenLookupState === 'found') {
      alert("⚠️ This UEN is already registered in our system! Please go back and use the 'Existing Client' form to register your trainees.");
      return;
    }

    // 🟢 CHECK AGREEMENT
    if (!formData.agree) {
      alert("Please accept the Terms and Conditions.");
      return;
    }

    setIsSubmitting(true);

    try {
      // 🟢 Execute reCAPTCHA v3
      const token = await executeRecaptcha();
      if (!token) {
        console.warn("reCAPTCHA token not generated, continuing anyway...");
      }

      // 🟢 Register in database via local PHP proxy
      const registerPayload = {
        sponsorType: "company",
        uen: formData.uen,
        industry: formData.industry,
        type: formData.type,
        agentCompany: formData.agentCompany,
        companyName: formData.companyName,
        companyAddress: formData.companyAddress,
        postalCode: formData.postalCode,
        billingAddress: formData.billingAddress,
        clientEmail: formData.clientEmail,
        contactPerson: formData.contactPerson,
        contactNumber: formData.contactNumber,
        schedule_id: courseData?.schedule?.schedule_id || null,
        course_id: courseData?.schedule?.course_id || null,
        location_id: courseData?.schedule?.location_id || 1,
        language: courseData?.schedule?.course_language || "English",
        course_type: "Public Class",
        discount: 0,
        promo_code: formData.promotionCode || "",
        staffName: formData.staffName || "",
        staffEmail: formData.staffEmail || "",
        trainees: students.map(t => ({
          studentName: t.name,
          nric: t.nric,
          idType: t.idType || "NRIC",
          cardType: t.idType || "NRIC",
          dob: t.dob || null,
          contact: t.contact,
          email: t.email,
          gender: t.gender || "",
          nationality: t.nationality || "",
          race: t.race || ""
        }))
      };

      console.log("💾 Registering new company sponsor and trainees in database via PHP proxy...", registerPayload);
      const regResponse = await fetch(REGISTER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(registerPayload)
      });

      if (!regResponse.ok) {
        throw new Error(`Failed to save registration to database: ${regResponse.statusText}`);
      }

      const regResult = await regResponse.json();
      console.log("✅ Database registration response:", regResult);
      if (!regResult.success) {
        throw new Error(regResult.error || "Failed to persist trainee registration in database.");
      }

      // Show success modal instead of alert
      setShowSuccessModal(true);

      // Auto-redirect to home page after 3 seconds for good UX
      setTimeout(() => {
        window.location.href = "/";
      }, 3000);

    } catch (error) {
      console.error("Submission error:", error);
      alert(`Error: ${error.message || 'Registration failed. Please try again or contact support.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 🔥 HANDLE MODAL CLOSE AND RESET
  const handleModalClose = () => {
    window.location.href = "/";
  };

  // ---------- Extraction helpers ----------
  const extractPdfText = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await getDocument({ data: arrayBuffer }).promise;
      let text = "";
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        const pageText = content.items.map((it) => it.str).join(" ");
        text += pageText + "\n";
      }
      return text;
    } catch (err) {
      console.error("PDF extract error:", err);
      return "";
    }
  };

  const extractDocxText = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value || "";
    } catch (err) {
      console.error("DOCX extract error:", err);
      return "";
    }
  };

  const extractExcelText = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
      return rows.map((r) => r.join(" ")).join("\n");
    } catch (err) {
      console.error("Excel extract error:", err);
      return "";
    }
  };

  const parseFieldsFromText = (text) => {
    const normalized = (text || "").replace(/\u00A0/g, " ").replace(/[\r]/g, "\n");

    const find = (patterns) => {
      for (const p of patterns) {
        const m = normalized.match(p);
        if (m && m[1]) return m[1].trim();
      }
      return "";
    };

    const name = find([/Name[:\-\s]+([A-Za-z ,.'-]+)/i, /Full Name[:\-\s]+([A-Za-z ,.'-]+)/i]);
    const nric = find([/NRIC[:\-\s]*([A-Za-z0-9-]+)/i, /IC[:\-\s]*([A-Za-z0-9-]+)/i, /ID[:\-\s]*([A-Za-z0-9-]+)/i]);
    const dobRaw = find([/DOB[:\-\s]*([\d\/\.\-]{6,20})/i, /Date of Birth[:\-\s]*([\d\/\.\-]{6,20})/i]);
    const email = (normalized.match(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i) || [null])[0] || "";
    const contact = find([/Contact(?: Number| No|):[:\-\s]*([0-9()+\-\s]+)/i, /Mobile[:\-\s]*([0-9()+\-\s]+)/i, /Phone[:\-\s]*([0-9()+\-\s]+)/i]);
    const gender = find([/Gender[:\-\s]*(Male|Female|M|F)/i]);

    let dob = "";
    if (dobRaw) {
      const d = dobRaw.trim();
      const dashParts = d.split(/[-\/\.]/).map(s => s.trim());
      if (dashParts.length === 3) {
        if (dashParts[0].length === 4) {
          dob = `${dashParts[0]}-${dashParts[1].padStart(2, "0")}-${dashParts[2].padStart(2, "0")}`;
        } else {
          dob = `20${dashParts[2].padStart(2, "0")}-${dashParts[1].padStart(2, "0")}-${dashParts[0].padStart(2, "0")}`;
        }
      } else {
        dob = d;
      }
    }

    return { name, nric, dob, email, contact, gender };
  };

  const handleSingleFileExtract = async (file, studentIndex) => {
    if (!file) return;
    setLoadingIndexes((s) => ({ ...s, [studentIndex]: true }));

    const ext = (file.name.split(".").pop() || "").toLowerCase();
    let text = "";

    try {
      if (ext === "pdf") {
        text = await extractPdfText(file);
      } else if (ext === "docx" || ext === "doc") {
        text = await extractDocxText(file);
      } else if (ext === "xlsx" || ext === "xls") {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonRows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        if (jsonRows && jsonRows.length > 0) {
          const row = jsonRows[0];
          const extracted = {
            name: row.Name || row["Full Name"] || row["name"] || row["Student Name"] || "",
            nric: row.NRIC || row["IC"] || row["ID"] || row["Id"] || "",
            dob: row.DOB || row["Date of Birth"] || row["dob"] || "",
            email: row.Email || row.email || "",
            contact: row.Contact || row.Phone || row["Contact Number"] || "",
            gender: row.Gender || row.gender || "",
            nationality: row.Nationality || "",
            race: row.Race || "",
          };
          setStudents((prev) => {
            const copy = [...prev];
            copy[studentIndex] = { ...copy[studentIndex], ...extracted };
            return copy;
          });
          setLoadingIndexes((s) => ({ ...s, [studentIndex]: false }));
          return;
        } else {
          text = await extractExcelText(file);
        }
      } else {
        alert("Unsupported file type for single student extract.");
        setLoadingIndexes((s) => ({ ...s, [studentIndex]: false }));
        return;
      }

      const parsed = parseFieldsFromText(text);
      setStudents((prev) => {
        const copy = [...prev];
        copy[studentIndex] = {
          ...copy[studentIndex],
          name: parsed.name || copy[studentIndex].name,
          nric: parsed.nric || copy[studentIndex].nric,
          dob: parsed.dob || copy[studentIndex].dob,
          email: parsed.email || copy[studentIndex].email,
          contact: parsed.contact || copy[studentIndex].contact,
          gender: parsed.gender || copy[studentIndex].gender,
        };
        return copy;
      });

    } catch (err) {
      console.error("Single extract failed:", err);
      alert("Failed to extract details from file.");
    } finally {
      setLoadingIndexes((s) => ({ ...s, [studentIndex]: false }));
    }
  };

  // ---------- Smart column detection ----------
  const smartColumnDetect = (row) => {
    const findValue = (possibleKeys) => {
      for (const key of Object.keys(row)) {
        const normalizedKey = key.toLowerCase().trim().replace(/\s+/g, ' ');
        for (const possibleKey of possibleKeys) {
          if (normalizedKey.includes(possibleKey.toLowerCase())) {
            const val = row[key];
            return val !== null && val !== undefined ? String(val).trim() : "";
          }
        }
      }
      return "";
    };

    const normalizeDob = (dateVal) => {
      if (!dateVal) return "";
      const str = String(dateVal).trim();

      if (/^\d{5}$/.test(str)) {
        const excelEpoch = new Date(1900, 0, 1);
        const days = parseInt(str) - 2;
        const date = new Date(excelEpoch.getTime() + days * 86400000);
        return date.toISOString().split('T')[0];
      }

      const parts = str.split(/[-\/\.]/);
      if (parts.length === 3) {
        if (parts[0].length === 4) return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
        if (parseInt(parts[0]) > 12) return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      }
      return str;
    };

    return {
      name: findValue(['name', 'student name', 'full name', 'trainee name']),
      idType: findValue(['id type', 'identification type']),
      nric: findValue(['nric', 'ic', 'passport', 'id', 'identification']),
      dob: normalizeDob(findValue(['dob', 'date of birth', 'birth date', 'birthday'])),
      email: findValue(['email', 'e-mail', 'email address']),
      gender: findValue(['gender', 'sex']),
      nationality: findValue(['nationality', 'country']),
      race: findValue(['race', 'ethnicity']),
      contact: findValue(['contact', 'phone', 'mobile', 'contact number', 'phone number']),
      educationCert: null,
      workPermit: null,
    };
  };

  // ---------- Bulk Excel handler ----------
  const handleBulkExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkLoading(true);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array", cellDates: true });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });

      if (!rows || !rows.length) {
        alert("Excel file appears empty or has no data rows");
        setBulkLoading(false);
        return;
      }

      const mapped = rows.map((row) => smartColumnDetect(row));

      const filtered = mapped.filter((stu) => {
        const hasName = stu.name && stu.name.length > 0;
        const notTest = !(stu.name.toLowerCase().includes('test') ||
          stu.name.toLowerCase().includes('bell 8'));
        return hasName && notTest;
      });

      // Dynamic capacity calculation
      const maxPax = courseData?.schedule?.max_trainee ? Number(courseData.schedule.max_trainee) : 20;
      const currentPax = courseData?.schedule?.current_trainee ? Number(courseData.schedule.current_trainee) : 0;
      const availableSlots = Math.max(0, maxPax - currentPax);

      const existingTrainees = students;
      const currentCount = existingTrainees.length;
      const incomingCount = filtered.length;

      if (currentCount + incomingCount > availableSlots) {
        const allowedNew = Math.max(0, availableSlots - currentCount);
        if (allowedNew === 0) {
           alert(`Maximum capacity reached! There are only ${availableSlots} slots left in this schedule.\nPlease book another schedule for the remaining trainees.`);
        } else {
           alert(`⚠️ Only ${availableSlots} slots available in this batch!\n\nYou already have ${currentCount} enrolled. We will only import the first ${allowedNew} trainees from this Excel file.\n\nPlease register the remaining trainees in a future or next batch.`);
        }
        
        const fitFiltered = filtered.slice(0, allowedNew);
        setStudents([...existingTrainees, ...fitFiltered]);
      } else {
        setStudents([...existingTrainees, ...filtered]);
        alert(`Successfully imported ${filtered.length} students from Excel`);
      }

    } catch (err) {
      console.error("Bulk excel error:", err);
      alert("Failed to read Excel file. Please ensure it's a valid .xlsx/.xls file with student data.");
    } finally {
      setBulkLoading(false);
      e.target.value = null;
    }
  };

  // 🟢 COURSE SUMMARY COMPONENT with employee calculation
  const renderCourseSummary = () => {
    if (!courseData?.title) return null;

    const basePrice = courseData.price || 0;
    const totalAmount = basePrice * students.length;
    const totalGst = totalAmount * 0.09;
    const grandTotal = totalAmount + totalGst;

    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-semibold text-green-800 mb-3 flex items-center gap-2"><ClipboardList size={20} /> Course Summary (Auto-calculated)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <div><span className="font-medium">Course:</span> {courseData.title}</div>
          <div><span className="font-medium">Level:</span> {courseData.selectedLevel?.toUpperCase() || 'N/A'}</div>
          <div><span className="font-medium">Duration:</span> {courseData.duration || 'N/A'}</div>
          <div><span className="font-medium">Base Price per trainee:</span> ${basePrice}</div>
          <div><span className="font-medium">Number of Trainees:</span> {students.length}</div>
          <div><span className="font-medium">Total Amount:</span> ${totalAmount.toFixed(2)}</div>
          <div><span className="font-medium">GST (9%):</span> ${totalGst.toFixed(2)}</div>
          <div><span className="font-medium text-green-700 font-bold">Grand Total:</span> ${grandTotal.toFixed(2)}</div>
        </div>
      </div>
    );
  };

  // ---------- UI ----------
  return (
    <>
      <AnimatePresence>
        {customAlert.isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
              className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-gray-100 overflow-hidden text-center flex flex-col p-6"
            >
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${customAlert.message.includes("Error") || customAlert.message.includes("Failed") || customAlert.message.includes("⚠️") || customAlert.message.includes("❌") ? 'bg-amber-100 text-amber-600' : 'bg-blue-100 text-blue-600'}`}>
                {customAlert.message.includes("Successfully") ? <CheckCircle2 size={32} /> : <AlertTriangle size={32} />}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Notification</h3>
              <p className="text-gray-600 mb-6 text-sm whitespace-pre-wrap">{customAlert.message}</p>
              <button
                onClick={() => setCustomAlert({ ...customAlert, isOpen: false })}
                className="w-full bg-gray-900 hover:bg-black text-white py-2.5 rounded-xl font-bold text-sm shadow-md transition-all active:scale-95"
              >
                Acknowledge
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-5xl mx-auto bg-white p-8 shadow-lg rounded-md mt-10 mb-20">
        <div className="space-y-8">

          {/* 🟢 AUTO-FILLED COURSE SUMMARY */}
          {renderCourseSummary()}

          <form onSubmit={handleSubmit}>
            {/* COMPANY DETAILS */}
            <section className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
              <h2 className="text-2xl font-bold text-slate-800 mb-6 pb-2 border-b-2 border-slate-100 flex items-center gap-3">
                <span className="bg-[#0b9949] text-white w-8 h-8 rounded-lg flex items-center justify-center text-sm">1</span>
                Company Details
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-wider mb-1">
                    Client Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="companyName"
                    placeholder="e.g. Greenmark Pte Ltd"
                    value={formData.companyName}
                    onChange={handleFormChange}
                    required
                    className={`border-2 rounded-xl px-4 py-3 w-full text-sm focus:outline-none transition-colors ${submitted && !formData.companyName ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-slate-200 focus:border-[#0b9949]'}`}
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-wider mb-1">
                    UEN <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      name="uen"
                      placeholder="e.g. 20101245A"
                      value={formData.uen}
                      onChange={handleFormChange}
                      required
                      className={`border-2 rounded-xl px-4 py-3 w-full text-sm focus:outline-none transition-colors ${uenLookupState === 'found' ? 'border-red-400 bg-red-50 focus:border-red-500' : submitted && !formData.uen ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-slate-200 focus:border-[#0b9949]'}`}
                    />
                    {uenLookupState === 'loading' && (
                      <Loader2 className="absolute right-4 top-3.5 animate-spin text-slate-400" size={18} />
                    )}
                    {uenLookupState === 'not_found' && (
                      <CheckCircle2 className="absolute right-4 top-3.5 text-[#0b9949]" size={18} />
                    )}
                  </div>
                  {uenLookupState === 'found' && (
                    <p className="text-xs text-red-500 font-bold mt-1.5 flex items-center gap-1">
                      <AlertTriangle size={12} /> This UEN is already registered! Please use the Existing Client form.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-wider mb-1">
                    Industry <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleFormChange}
                    required
                    className={`border-2 rounded-xl px-4 py-3 w-full text-sm focus:outline-none transition-colors ${submitted && !formData.industry ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-slate-200 focus:border-[#0b9949]'}`}
                  >
                    <option value="" disabled>Select Industry</option>
                    <option value="Construction">Construction</option>
                    <option value="Marine">Marine</option>
                    <option value="Shipyard">Shipyard</option>
                    <option value="Manufacturing">Manufacturing</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-wider mb-1">
                    Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleFormChange}
                    required
                    className={`border-2 rounded-xl px-4 py-3 w-full text-sm focus:outline-none transition-colors ${submitted && !formData.type ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-slate-200 focus:border-[#0b9949]'}`}
                  >
                    <option value="" disabled>Select Type</option>
                    <option value="Corporate">Corporate</option>
                    <option value="Non-member">Non-member</option>
                    <option value="Blacklist">Blacklist</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-wider mb-1">
                    Company Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="companyAddress"
                    placeholder="Enter full address"
                    value={formData.companyAddress}
                    onChange={handleFormChange}
                    required
                    className={`border-2 rounded-xl px-4 py-3 w-full text-sm focus:outline-none transition-colors ${submitted && !formData.companyAddress ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-slate-200 focus:border-[#0b9949]'}`}
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-wider mb-1">
                    Postal Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="postalCode"
                    placeholder="e.g. 609916"
                    value={formData.postalCode}
                    onChange={handleFormChange}
                    required
                    className={`border-2 rounded-xl px-4 py-3 w-full text-sm focus:outline-none transition-colors ${submitted && !formData.postalCode ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-slate-200 focus:border-[#0b9949]'}`}
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-wider mb-1">
                    Agent / Holding Company
                  </label>
                  <input
                    name="agentCompany"
                    placeholder="e.g. Acme Holding Group"
                    value={formData.agentCompany}
                    onChange={handleFormChange}
                    className="border-2 rounded-xl px-4 py-3 w-full text-sm focus:outline-none transition-colors border-slate-200 focus:border-[#0b9949]"
                  />
                </div>

                <div className="col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">
                      Billing Address <span className="text-red-500">*</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        id="sameAsCompany"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData(prev => ({ ...prev, billingAddress: formData.companyAddress }));
                          } else {
                            setFormData(prev => ({ ...prev, billingAddress: "" }));
                          }
                        }}
                        className="rounded border-slate-300 text-[#0b9949] focus:ring-[#0b9949]"
                      />
                      <span className="text-[10px] font-bold text-[#0b9949] uppercase tracking-wider">
                        Same as Company Address
                      </span>
                    </label>
                  </div>
                  <textarea
                    name="billingAddress"
                    placeholder="Enter billing address"
                    value={formData.billingAddress}
                    onChange={handleFormChange}
                    required
                    className={`border-2 rounded-xl px-4 py-3 w-full text-sm focus:outline-none transition-colors ${submitted && !formData.billingAddress ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-slate-200 focus:border-[#0b9949]'}`}
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-wider mb-1">
                    Email ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="clientEmail"
                    placeholder="company@domain.com"
                    type="email"
                    value={formData.clientEmail}
                    onChange={handleFormChange}
                    required
                    className={`border-2 rounded-xl px-4 py-3 w-full text-sm focus:outline-none transition-colors ${submitted && !formData.clientEmail ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-slate-200 focus:border-[#0b9949]'}`}
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-wider mb-1">
                    Contact Person <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="contactPerson"
                    placeholder="Full Name"
                    value={formData.contactPerson}
                    onChange={handleFormChange}
                    required
                    className={`border-2 rounded-xl px-4 py-3 w-full text-sm focus:outline-none transition-colors ${submitted && !formData.contactPerson ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-slate-200 focus:border-[#0b9949]'}`}
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-wider mb-1">
                    Contact Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    inputMode="tel"
                    name="contactNumber"
                    placeholder="e.g. +65 9123 4567"
                    value={formData.contactNumber}
                    onChange={handleFormChange}
                    required
                    onKeyPress={(e) => {
                      if (!/[0-9+\-()\s]/.test(e.key)) e.preventDefault();
                    }}
                    className={`border-2 rounded-xl px-4 py-3 w-full text-sm focus:outline-none transition-colors ${submitted && !formData.contactNumber ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-slate-200 focus:border-[#0b9949]'}`}
                  />
                </div>
              </div>

              <div className="mt-6 text-sm text-red-500 font-medium space-y-2">
                <p>For new corporate client, kindly fill company profile detail in order to track the registrations sponsored by your company. Please note that a valid UEN is required.</p>
                <p>Client accounts are subject to review and approval within 2 working day. We appreciate your patience, and will notify you once your account has been successfully created.</p>
              </div>
            </section>





            {/* Staff Referral */}
            <section className="bg-slate-50 rounded-2xl p-6 border border-slate-200 mt-10">
              <div className="mb-4">
                <label className="block font-medium mb-2 text-gray-800">Do you know our staff?</label>
                <div className="flex gap-6">
                  <label className="flex items-center space-x-2 cursor-pointer font-medium text-gray-700">
                    <input
                      type="radio"
                      name="knowStaff"
                      value="Yes"
                      checked={formData.knowStaff === "Yes"}
                      onChange={handleFormChange}
                      className="w-4 h-4 text-[#0b9949] focus:ring-[#0b9949] border-gray-300"
                    />
                    <span>Yes</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer font-medium text-gray-700">
                    <input
                      type="radio"
                      name="knowStaff"
                      value="No"
                      checked={formData.knowStaff === "No"}
                      onChange={handleFormChange}
                      className="w-4 h-4 text-[#0b9949] focus:ring-[#0b9949] border-gray-300"
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>

              <AnimatePresence>
                {formData.knowStaff === "Yes" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="grid md:grid-cols-2 gap-6 mt-2 pt-2 pb-4">
                      {/* Staff Name Input */}
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Sales Staff Name *</label>
                        <div className="relative">
                          <input
                            type="text"
                            name="staffName"
                            placeholder="e.g. jack"
                            value={formData.staffName}
                            onChange={handleFormChange}
                            required={formData.knowStaff === "Yes"}
                            autoComplete="off"
                            className={`border-2 rounded-xl px-4 py-3 w-full text-sm font-semibold tracking-wide pr-12 transition-all duration-300 outline-none
                              ${staffLookupState === 'found' ? 'border-emerald-400 bg-emerald-50/30 focus:border-emerald-500' : 
                                staffLookupState === 'not_found' ? 'border-amber-300 bg-amber-50/20 focus:border-amber-400' : 
                                staffLookupState === 'error' ? 'border-red-300 bg-red-50/20' :
                                'border-slate-200 bg-white focus:border-[#0b9949]'}`}
                          />
                          {/* Status Icon */}
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-base">
                            {staffLookupState === 'loading' && (
                              <svg className="animate-spin w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                              </svg>
                            )}
                            {staffLookupState === 'found' && <Check className="text-emerald-500 stroke-[3]" size={20} />}
                            {staffLookupState === 'not_found' && <span className="text-amber-400 font-bold text-lg">?</span>}
                            {staffLookupState === 'error' && <span className="text-red-400 font-bold text-lg">!</span>}
                          </div>
                        </div>

                        {/* Live status message */}
                        <AnimatePresence mode="wait">
                          {staffLookupState === 'found' && (
                            <motion.div
                              key="found"
                              initial={{ opacity: 0, y: -6, height: 0 }}
                              animate={{ opacity: 1, y: 0, height: 'auto' }}
                              exit={{ opacity: 0, y: -6, height: 0 }}
                              className="mt-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-3"
                            >
                              <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center shrink-0 shadow-sm">
                                <Check className="text-white" size={14} strokeWidth={4} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-extrabold text-emerald-800 uppercase tracking-wide">Valid Sales Staff</p>
                                <p className="text-sm font-bold text-emerald-700 mt-0.5">{staffOfficialName}</p>
                                <p className="text-[10px] text-emerald-600 mt-0.5 flex items-center gap-1">Official sales email auto-filled <ArrowDown size={12} /></p>
                              </div>
                            </motion.div>
                          )}
                          {staffLookupState === 'not_found' && (
                            <motion.div
                              key="not_found"
                              initial={{ opacity: 0, y: -6, height: 0 }}
                              animate={{ opacity: 1, y: 0, height: 'auto' }}
                              exit={{ opacity: 0, y: -6, height: 0 }}
                              className="mt-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 flex items-center gap-2.5"
                            >
                              <div className="w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center shrink-0 shadow-sm">
                                <span className="text-white text-xs font-extrabold">?</span>
                              </div>
                              <div>
                                <p className="text-xs font-extrabold text-amber-800 uppercase tracking-wide">Not Found</p>
                                <p className="text-sm font-semibold text-amber-700 mt-0.5">Sales staff name not found in system.</p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Staff Email Input */}
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Sales Staff Email</label>
                        <input
                          type="email"
                          name="staffEmail"
                          placeholder="Sales Staff Email (e.g. name@greenmark.com.sg)"
                          value={formData.staffEmail}
                          onChange={handleFormChange}
                          readOnly={staffLookupState === 'found'}
                          className={`border-2 rounded-xl px-4 py-3 w-full text-sm font-semibold tracking-wide transition-all duration-300 outline-none
                            ${staffLookupState === 'found' 
                              ? 'border-emerald-300 bg-gray-50 text-gray-500 cursor-not-allowed' 
                              : 'border-slate-200 bg-white focus:border-[#0b9949]'}`}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </section>

            {/* DECLARATION */}
            <div className="mt-6">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="agree"
                  checked={formData.agree}
                  onChange={handleFormChange}
                />
                <span>
                  I accept the{" "}Terms and Conditions
                 
                </span>
              </label>
            </div>

            {/* 🟢 reCAPTCHA v3 Badge (required by Google) */}
            <div className="grecaptcha-badge" data-style="inline"></div>

            {/* SUBMIT BUTTON */}
            <div className="text-center mt-8">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={!formData.agree || isSubmitting}
                className={`w-full py-4 px-6 rounded-xl mt-8 font-bold text-lg shadow-lg transition-all duration-300 flex items-center justify-center gap-2 ${formData.agree && !isSubmitting
                  ? "bg-[#0b9949] hover:bg-[#097c3c] text-white cursor-pointer hover:shadow-xl hover:-translate-y-1"
                  : "bg-slate-200 text-slate-500 cursor-not-allowed opacity-80"
                  }`}
              >
                {!formData.agree ? <Lock size={20} /> : (isSubmitting ? <Loader2 size={20} className="animate-spin" /> : <CheckCircle2 size={20} />)}
                {isSubmitting ? "Processing Registration..." : (formData.agree ? "Confirm Registration" : "Accept Terms to Submit")}
              </motion.button>
            </div>
          </form>
        </div>
      </div>

      {/* Success Modal */}
      <AnimatePresence>
        {showSuccessModal && (
          <SuccessModal onClose={handleModalClose} />
        )}
      </AnimatePresence>


    </>
  );
}